import { ChangeDetectorRef, Component, Input, OnChanges, SimpleChanges } from "@angular/core";
import { BaseChartComponent } from "./base-chart.component";
import { ChartConfiguration, Plugin } from "chart.js/auto";
import { AlertLevels, DataPointDto } from "src/app/services/api";
import { TranslateService } from "@ngx-translate/core";
import {
  CHART_LEVEL1_ZONE_COLOR,
  CHART_LEVEL2_ZONE_COLOR,
  CHART_LEVEL3_ZONE_COLOR,
  CHART_LEVEL4_ZONE_COLOR,
  CHART_CURRENT_TIME_WIDTH,
  GRID_LINES_COLOR,
  PRIMARY_LINE_COLOR,
  SECONDARY_LINE_COLOR,
} from "src/app/constants/index";

@Component({
  selector: "app-discharge-chart",
  templateUrl: "./chart.component.html",
  styleUrls: ["./chart.component.scss"],
})
export class DischargeChartComponent extends BaseChartComponent implements OnChanges {
  @Input() public alertLevels!: AlertLevels;
  @Input() public simulationData?: DataPointDto[];
  @Input() public observedData?: DataPointDto[];

  private observedDataLabel: string;
  private simulationDataLabel: string;

  constructor(translate: TranslateService, cdr: ChangeDetectorRef) {
    super(translate, cdr);
    this.observedDataLabel = translate.instant("STATION_PAGE.OBSERVED_DISCHARGE");
    this.simulationDataLabel = translate.instant("STATION_PAGE.PREDICTED_DISCHARGE");
  }

  public ngOnChanges(changes: SimpleChanges): void {
    // Update the data property for app-empty-data component
    // This ensures that if either observedData or simulationData exists, the empty data message won't show
    if (this.observedData?.length || this.simulationData?.length) {
      // Use observedData as primary data source if available, otherwise use simulationData
      this.data = this.observedData?.length ? this.observedData : this.simulationData || [];

      // Force chart re-rendering when data changes
      if ((changes.observedData || changes.simulationData) && this.chartContainer) {
        setTimeout(() => {
          this.renderChart();
          this.cdr.detectChanges();
        }, 0);
        return;
      }
    }

    // Call parent's ngOnChanges to ensure base component logic runs
    super.ngOnChanges(changes);
  }

  // Utiliza el plugin unificado de la clase base con el tipo "discharge"
  private drawDischargeAlertLevelsPlugin = this.drawAlertLevelsPlugin("discharge");

  protected getChartConfig(): ChartConfiguration {
    const xSuggestedMax = !this.currentTime ? new Date().getTime() : null;
    const ySuggestedMax = this.alertLevels?.level3Discharge || this.alertLevels?.level2Discharge || this.alertLevels?.level1Discharge || 2;

    // Get common chart options with m³/s as unit
    const commonOptions = this.getCommonChartOptions(xSuggestedMax, ySuggestedMax, " m³/s");

    // Transform observed data if provided
    const transformedObservedData = this.observedData?.map(point => {
      const timestamp = new Date(point.timestamp).getTime();
      if (point.value === null) {
        return { x: timestamp, y: null };
      } else {
        return { x: timestamp, y: Math.max(point.value, 0) };
      }
    });

    // Transform simulation data if provided
    const transformedSimulationData = this.simulationData?.map(point => {
      const timestamp = new Date(point.timestamp).getTime();
      if (point.value === null) {
        return { x: timestamp, y: null };
      } else {
        return { x: timestamp, y: Math.max(point.value, 0) };
      }
    });

    // Determine which datasets to show based on available data
    const datasets = [];

    // Case 1: We have both observed and simulation data
    if (transformedObservedData?.length && transformedSimulationData?.length) {
      this.showCurrentTimeLine = true;
      datasets.push(
        {
          data: transformedObservedData,
          label: this.observedDataLabel,
          borderColor: PRIMARY_LINE_COLOR,
          pointBackgroundColor: PRIMARY_LINE_COLOR,
          backgroundColor: PRIMARY_LINE_COLOR,
          borderWidth: 1.5,
          pointRadius: 1,
          tension: 0.5,
          parsing: false,
        },
        {
          data: transformedSimulationData,
          label: this.simulationDataLabel,
          borderColor: SECONDARY_LINE_COLOR,
          pointBackgroundColor: SECONDARY_LINE_COLOR,
          backgroundColor: SECONDARY_LINE_COLOR,
          borderWidth: 1.5,
          pointRadius: 1,
          tension: 0.5,
          parsing: false,
        },
      );
    } else if (!transformedObservedData?.length && transformedSimulationData?.length) {
      // Case 2: We have only simulation data
      this.showCurrentTimeLine = true;
      datasets.push({
        data: transformedSimulationData,
        label: this.simulationDataLabel,
        borderColor: SECONDARY_LINE_COLOR,
        pointBackgroundColor: SECONDARY_LINE_COLOR,
        backgroundColor: SECONDARY_LINE_COLOR,
        borderWidth: 1.5,
        pointRadius: 1,
        tension: 0.5,
        parsing: false,
      });
    } else if (transformedObservedData?.length) {
      // Case 3: We only have observed data
      datasets.push({
        data: transformedObservedData,
        label: this.observedDataLabel,
        borderColor: PRIMARY_LINE_COLOR,
        backgroundColor: PRIMARY_LINE_COLOR,
        borderWidth: 2,
        pointRadius: 1,
        tension: 0.5,
        parsing: false,
      });
    }

    // Show legend only if we have multiple datasets
    const showLegend = datasets.length > 1;
    const plugins = [this.drawDischargeAlertLevelsPlugin];

    // Add current time line plugin if showing simulation data
    if (this.showCurrentTimeLine) {
      plugins.push(this.drawCurrentTimeLinePlugin);
    }

    // Add legend spacing plugin if showing legend
    if (showLegend) {
      plugins.push(this.legendSpacingPlugin);
    }

    return {
      type: "line",
      data: {
        datasets: datasets,
      },
      options: {
        ...commonOptions,
        plugins: showLegend
          ? {
            legend: {
              display: true,
              labels: {
                boxHeight: 2,
                boxWidth: 24,
              },
            },
          }
          : {},
      },
      plugins: plugins,
    };
  }
}
